import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Card,
  Page,
  Pagination,
  LoadingModal,
  Filter,
  defaultValues,
  FilterDefaultValues,
  LoadingHavaianas
} from 'components'
import { DraftProps } from 'container/Home/components/Banners/interface'
import * as S from './styles'
import { toast } from 'react-toastify'
import { BodyRequest, getGerenciarOVListaVddr } from 'services/manage_ov'
import _ from 'lodash'
import axios from 'axios'
import { resetSalesOrder, setCheckedOvs } from 'store/SalesOrderContext/salesOrderSlice'
import useParams, { ParamsType } from 'commons/customHooks/useParams'
import { setViewByFilter } from 'store/StructuralContext'
import { useDispatch, useSelector } from 'store/store'
import { SapOptionsToOvStatus } from 'store/OVContext/ovContextTypes'
import ViewBySalesOrder from './components/ViewBySalesOrder'
import { FormatBodyType, formatRequestBodyByFilters } from './utils'
import ViewByOv from './components/ViewByOv'
import ViewByOrderAnticipation from './components/ViewByOrderAnticipation'
import { getComercialStructure } from 'store/ManagementContext/managementSlice'
import { formatDate, getFirstDayOfMonth } from 'commons/utils/date'
import { useAbortController } from 'commons/customHooks'

export interface ExecutiveContentProps {
  draftsData: DraftProps[]
}

export type ViewByType = 'ovs' | 'orders' | 'orderAnticipation'

const ManageOrders = () => {
  const firstPage = 1
  const dispatch = useDispatch()
  const { resetController, isCurrentRequest } = useAbortController()
  const [searchParams] = useSearchParams()
  const { isLoadingOVs } = useSelector(({ salesOrder }) => salesOrder)
  const { viewByFilter } = useSelector((state) => state.structure)
  const { loadingStructure } = useSelector((state) => state.management)
  const { preferences } = useSelector((state) => state.auth)
  const [viewBy, setViewBy] = useState<ViewByType>(viewByFilter)
  const [loading, setIsLoading] = useState(true)
  const [draftsData, setDraftsData] = useState<DraftProps[]>([])
  const [draftsPage, setDraftsPage] = useState(0)
  const [qtPage, setQtPage] = useState(0)
  const [totalDrafts, setTotalDrafts] = useState(0)

  const [ovsSapData, setOvsSapData] = useState<SapOptionsToOvStatus>(
    {} as SapOptionsToOvStatus
  )
  const { createParamsByObject, getParams } = useParams()

  const PAGE_SIZE = 10

  // NOTE: Create the request Body data to get the orders data
  const getOrdersData = async (body: FormatBodyType = {}) => {
    if (draftsPage > 0) {
      setIsLoading(true)

      const { controller, requestId } = resetController()

      const payload = formatRequestBodyByFilters(body, false, viewBy)

      if (viewBy === 'ovs') dispatch(setCheckedOvs([]))

      const selectedViewBy = viewBy === 'orderAnticipation' ? 'orders' : viewBy

      await getGerenciarOVListaVddr(
        selectedViewBy,
        {
          Paginas: (body.page && body.page - 1) || 0,
          Linhas: PAGE_SIZE
        },
        payload as BodyRequest,
        controller.signal
      )
        .then((response) => {
          if (response.data) {
            setDraftsData(() =>
              response.data.map((res: DraftProps) => ({
                ...res,
                checked: false
              }))
            )
            setQtPage(Math.ceil(Number(response.data[0].RowsCount) / PAGE_SIZE))
            setTotalDrafts(Number(response.data[0].RowsCount))
          } else {
            setDraftsData([])
            setQtPage(0)
            setTotalDrafts(0)
          }
        })
        .catch((error) => {
          if (!axios.isCancel(error)) {
            toast.error(`${error?.response?.data}`)
          }
        })
        .finally(() => {
          if (isCurrentRequest(requestId)) {
            setIsLoading(false)
          }
        })
    }
  }

  const priorityFields = [
    'numeroPedidoRascunho',
    'numeroOrdemVenda',
    'numeroOrdemVendaSAP',
    'numeroPedidoCliente',
    'documentoFatura',
    'numeroNotaFiscal',
    'numeroPedidoEDI'
  ]

  useEffect(() => {
    const startDate = getFirstDayOfMonth()
    const endDate = formatDate(new Date())
    const params = getParams()

    const hasPriorityValue = Object.keys(params).some((value) =>
      priorityFields.includes(value)
    )
    const defaultInterval = hasPriorityValue ? '' : `${startDate} - ${endDate}`
    const {
      CanalDistribuicao: Mercado,
      RegiaoVenda,
      EscritorioVenda,
      EquipeVenda
    } = preferences

    const payload = {
      ...params,
      periodo: params.periodo ?? defaultInterval,
      mercado: params.mercado ?? Mercado ?? '',
      escritorioVenda: params.escritorioVenda ?? EscritorioVenda ?? '',
      equipeVenda: params.equipeVenda ?? EquipeVenda ?? '',
      codigoZona: params.codigoZona ?? RegiaoVenda ?? ''
    }
    getOrdersData(payload)
  }, [viewBy])

  useEffect(() => {
    const paramsPage = searchParams.get('page')
    setDraftsPage((oldDraftsPage) => {
      if (paramsPage !== null) {
        if (oldDraftsPage !== +paramsPage) return +paramsPage
      }
      return firstPage
    })
    dispatch(resetSalesOrder())
  }, [])

  useEffect(() => {
    if (draftsPage > 0) {
      const startDate = getFirstDayOfMonth()
      const endDate = formatDate(new Date())
      let newParams = getParams()

      const hasPriorityValue = Object.keys(newParams).some((value) =>
        priorityFields.includes(value)
      )
      const defaultInterval = hasPriorityValue ? '' : `${startDate} - ${endDate}`
      const {
        CanalDistribuicao: Mercado,
        RegiaoVenda,
        EscritorioVenda,
        EquipeVenda
      } = preferences

      newParams = {
        ...newParams,
        page: draftsPage.toString(),
        periodo: newParams.periodo ?? defaultInterval,
        mercado: newParams.mercado ?? (hasPriorityValue ? '' : Mercado ?? ''),
        escritorioVenda:
          newParams.escritorioVenda ?? (hasPriorityValue ? '' : EscritorioVenda ?? ''),
        equipeVenda: newParams.equipeVenda ?? (hasPriorityValue ? '' : EquipeVenda ?? ''),
        codigoZona: newParams.codigoZona ?? (hasPriorityValue ? '' : RegiaoVenda ?? '')
      }

      getOrdersData(newParams)
      newParams.page = draftsPage.toString()
      createParamsByObject(newParams as unknown as ParamsType)
    }
  }, [draftsPage])

  useEffect(() => {
    dispatch(getComercialStructure())
    const paramsPage = searchParams.get('page')
    if (viewByFilter === 'ovs') {
      handleViewByChange('ovs', paramsPage || '0')
    }
  }, [])

  const handleFilterTable = (data?: FilterDefaultValues) => {
    const arrayCodStatusOV = data?.codigosStatusOrdemVenda
      ? data.codigosStatusOrdemVenda.map((item: string) => +item.split('-')[0])
      : []
    let paramsObject = {}

    if (data) {
      if (
        data.numeroPedidoRascunho !== '' ||
        data.numeroOrdemVenda !== '' ||
        data.numeroOrdemVendaSAP !== '' ||
        data.numeroPedidoCliente !== '' ||
        data.documentoFatura !== '' ||
        data.numeroNotaFiscal !== '' ||
        data.numeroPedidoEDI !== ''
      ) {
        const clearedData = _.omit(data, 'periodo')
        paramsObject = {
          ...clearedData,
          page: firstPage,
          codigosStatusOrdemVenda: arrayCodStatusOV.join(','),
          codigosStatusPedido: data?.codigosStatusPedido?.join(','),
          codigosCentros: data?.codigosCentros?.join(','),
          searchBox: data.searchBox.toLocaleUpperCase(),
          numeroOrdemVenda: data.numeroOrdemVenda.toLocaleUpperCase(),
          numeroPedidoCliente: data.numeroPedidoCliente.toLocaleUpperCase(),
          numeroPedidoRascunho: data.numeroPedidoRascunho.toLocaleUpperCase(),
          numeroOrdemVendaSAP: data.numeroOrdemVendaSAP.toLocaleUpperCase(),
          numeroPedidoEDI: data.numeroPedidoEDI.toLocaleUpperCase(),
          codigosCores: data.codigosCores,
          mercado: data.mercado.join(','),
          escritorioVenda: data.escritorioVenda.join(','),
          equipeVenda: data.equipeVenda.join(','),
          codigoZona: data?.codigoZona?.join(','),
          documentoFatura: data.documentoFatura.toLocaleUpperCase(),
          caracteristicas: data?.caracteristicas?.join(',')
        }
      } else {
        paramsObject = {
          ...data,
          page: firstPage,
          codigosStatusOrdemVenda: arrayCodStatusOV.join(','),
          codigosStatusPedido: data?.codigosStatusPedido?.join(','),
          codigosCentros: data?.codigosCentros?.join(','),
          searchBox: data.searchBox.toLocaleUpperCase(),
          numeroOrdemVenda: data.numeroOrdemVenda.toLocaleUpperCase(),
          numeroPedidoCliente: data.numeroPedidoCliente.toLocaleUpperCase(),
          numeroPedidoRascunho: data.numeroPedidoRascunho.toLocaleUpperCase(),
          numeroOrdemVendaSAP: data.numeroOrdemVendaSAP.toLocaleUpperCase(),
          numeroPedidoEDI: data.numeroPedidoEDI.toLocaleUpperCase(),
          codigosCores: data.codigosCores,
          mercado: data.mercado.join(','),
          escritorioVenda: data.escritorioVenda.join(','),
          equipeVenda: data.equipeVenda.join(','),
          codigoZona: data?.codigoZona?.join(','),
          documentoFatura: data.documentoFatura.toLocaleUpperCase(),
          caracteristicas: data?.caracteristicas?.join(',')
        }
      }
    }
    createParamsByObject(paramsObject as unknown as ParamsType)
    setDraftsPage(firstPage)

    const payload = {
      ...paramsObject,
      codigosStatusOrdemVenda: arrayCodStatusOV,
      codigosStatusPedido: data?.codigosStatusPedido,
      codigosCores: data?.codigosCores ? data?.codigosCores.split(',') : [],
      materialMTO: (data?.materialMTO as number) ?? null
    }

    getOrdersData(payload)
  }

  const handleViewByChange = (value: ViewByType, page?: string) => {
    setIsLoading(true)
    if (!page) {
      setDraftsPage(firstPage)
    }
    setViewBy(value)
    dispatch(setViewByFilter(value))
  }

  const handleClearFilter = () => {
    if (viewBy === 'ovs') {
      handleViewByChange('orders')
    } else {
      const startDate = getFirstDayOfMonth()
      const endDate = formatDate(new Date())
      const defaultInterval = `${startDate} - ${endDate}`

      setDraftsPage(firstPage)

      const {
        CanalDistribuicao: Mercado,
        RegiaoVenda,
        EscritorioVenda,
        EquipeVenda
      } = preferences
      const payload = {
        mercado: Mercado ? [Mercado] : [],
        escritorioVenda: EscritorioVenda ? [EscritorioVenda] : [],
        equipeVenda: EquipeVenda ? [EquipeVenda] : [],
        codigoZona: RegiaoVenda ? [RegiaoVenda] : []
      }

      const newParams = {
        mercado: Mercado ?? '',
        escritorioVenda: EscritorioVenda ?? '',
        equipeVenda: EquipeVenda ?? '',
        codigoZona: RegiaoVenda ?? ''
      }
      createParamsByObject(newParams as ParamsType)

      getOrdersData({
        ...payload,
        page: firstPage,
        periodo: defaultInterval
      })
    }
  }

  useEffect(() => {
    const params = getParams()
    if (isLoadingOVs) {
      handleFilterTable(defaultValues(params, viewBy))
    }
  }, [isLoadingOVs])

  const handleChangeFilter = () => {
    getOrdersData({ page: draftsPage })
  }

  return (
    <Page fullwidth={true}>
      {loadingStructure ? (
        <LoadingModal />
      ) : (
        <S.Container>
          <Filter
            onSubmit={handleFilterTable}
            viewBy={viewBy}
            setViewBy={handleViewByChange}
            disabledsButtons={['codigosStatusEstoque']}
            onChangeFilter={handleChangeFilter}
            onClearFilter={handleClearFilter}
          />

          <Card>
            {loading ? (
              <LoadingHavaianas />
            ) : (
              <>
                {viewBy === 'orders' && (
                  <ViewBySalesOrder
                    setDraftsData={setDraftsData}
                    draftsData={draftsData}
                    getOrdersData={getOrdersData}
                    draftsPage={draftsPage}
                  />
                )}
                {viewBy === 'ovs' && (
                  <ViewByOv
                    draftsData={draftsData}
                    setOvsSapData={setOvsSapData}
                    setDraftsData={setDraftsData}
                    ovsSapData={ovsSapData}
                  />
                )}
                {viewBy === 'orderAnticipation' && (
                  <ViewByOrderAnticipation
                    setDraftsData={setDraftsData}
                    draftsData={draftsData}
                    getOrdersData={getOrdersData}
                    draftsPage={draftsPage}
                  />
                )}
                {totalDrafts > 0 && (
                  <>
                    <S.PaginationWrapper>
                      <Pagination
                        currentPage={draftsPage}
                        setCurrentPage={setDraftsPage}
                        numberOfPages={qtPage}
                        numberOfItems={totalDrafts}
                        itemsPerPage={PAGE_SIZE}
                      />
                    </S.PaginationWrapper>
                  </>
                )}
              </>
            )}
          </Card>
        </S.Container>
      )}
    </Page>
  )
}

export default ManageOrders
